import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "./ChatBox.scss";
import {
  Avatar,
  Button,
  Col,
  Flex,
  Input,
  Row,
  Skeleton,
  Tabs,
  TabsProps,
  Upload,
  Image,
  Tooltip,
  Divider,
} from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  DownloadOutlined,
  ExpandOutlined,
  FieldTimeOutlined,
  LoadingOutlined,
  PushpinFilled,
  PushpinOutlined,
  SendOutlined,
  ShoppingCartOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import attachIcon from "@assets/icons/attach.png";
import imageIcon from "@assets/icons/image.png";
import emojiIcon from "@assets/icons/emoji.png";
import TextArea from "antd/es/input/TextArea";
import Helpers from "@utils/Helpers";
import { useUser } from "contexts/UserContext";
import { conversationApi } from "services/ConversationApi";
import { errorLog } from "logging";
import { usePusher } from "contexts/PusherContext";
import EmojiPicker, { EmojiStyle } from "emoji-picker-react";
import Validates from "@utils/Validates";
import excelIcon from "@assets/icons/xls.png";
import pdfIcon from "@assets/icons/pdf.png";
import docsIcon from "@assets/icons/doc.png";
import { useUnit } from "contexts/UnitContext";
import { useCart } from "contexts/CartContext";
import { Link } from "react-router-dom";

interface IConversaction {
  message: string | null;
  attachment_urls?: string[];
  files?: any[];
  time: string;
  is_owner: boolean;
}

interface IConversationList {
  conversation_id: number;
  message: string;
  is_viewed: boolean;
  is_owner: boolean;
  time: string;
  is_pinned?: boolean;
  target_user: any;
}

const ChatBox: React.FC = () => {
  const {
    setIsOpenChatbox,
    isOpenChatbox,
    selectedReceiver,
    setSelectedReceiver,
  } = useUser();
  const [conversation, setConversation] = useState<IConversaction[]>([]);
  const [messageToSend, setMessageToSend] = useState("");
  const [showPicker, setShowPicker] = useState(false);
  const [conversationList, setConversationList] = useState<any>([]);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [isSendingImage, setIsSendingImage] = useState(false);
  const { message } = usePusher();
  const { units, convertCurrency } = useUnit();
  const { playNoti, setFetchCount } = useUser();
  const { addToCart } = useCart();
  const pickerRef: any = useRef(null);
  const conversationIdRef = useRef(0);
  const textBoxRef: any = useRef(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState({
    conversationList: false,
    conversationDetail: false,
  });

  // CLOSE EMOJI
  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setShowPicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // HANDLE UPDATE SEEN
  const handleUpdateSeen = useCallback(async () => {
    if (!selectedReceiver?.conversationId) return;

    const newList = conversationList?.map((item: any) =>
      item.conversation_id == selectedReceiver.conversationId
        ? { ...item, is_viewed: true }
        : item
    );
    setConversationList(newList);
    try {
      await conversationApi.markViewed({
        conversation_id: selectedReceiver.conversationId,
      });
    } catch (error) {
      errorLog("ChatBox.tsx - handleUpdateSeen", error);
    }
  }, [selectedReceiver?.conversationId, conversationList]);

  // HANDLE PUSHER
  useEffect(() => {
    if (message && message.module == "Message") {
      if (isOpenChatbox) {
        if (
          selectedReceiver.conversationId === message.message.conversation_id
        ) {
          const newMessage = {
            message: message.message.message,
            attachment_urls: message.message.attachment_urls,
            time: message.message.time,
            is_owner: message.message.is_owner,
          };
          setConversation((prevMessages) => [newMessage, ...prevMessages]);
          handleUpdateSeen();
        } else {
          setFetchCount(true);
          playNoti();
        }
        message.message.is_viewed =
          selectedReceiver.conversationId === message.message.conversation_id
            ? true
            : false;
        handleUpdateConversationList(message.message);
      } else {
        setFetchCount(true);
        playNoti();
      }
    }
  }, [message]);

  // HANDLE GET CONVERSATION DETAIL
  useEffect(() => {
    const getConversation = async () => {
      try {
        const response = await conversationApi.getConversationMessages(
          selectedReceiver.conversationId!
        );
        if (response.status == 200) {
          setConversation(response.data.data.messages);
          if (
            response.data.data.referenceProduct &&
            selectedReceiver.referenceProduct == null
          ) {
            const referenceProduct = response.data.data.referenceProduct;
            setSelectedReceiver({
              ...selectedReceiver,
              referenceProduct: {
                productId: referenceProduct.product_id,
                productType: referenceProduct.product_type,
                productImage: referenceProduct.product_image,
                quantity: referenceProduct.quantity,
                weightPerBale: referenceProduct.weight_per_bale,
                pricePerUnit:
                  referenceProduct.unit == "Ton"
                    ? referenceProduct.price_per_tons
                    : referenceProduct.price_per_bales,
              },
            });
          }
        }
      } catch (error) {
        Helpers.notification.error("Something wrong in server");
        errorLog("ChatBox.tsx - getConversation", error);
      }
      setIsLoading({ ...isLoading, conversationDetail: false });
    };
    if (selectedReceiver.conversationId) {
      setIsLoading({ ...isLoading, conversationDetail: true });
      textBoxRef.current.focus();
      if (messageToSend !== "") {
        storeMessageToDraft();
      }
      conversationIdRef.current = selectedReceiver.conversationId;
      handleGetMessageFromDraft();
      getConversation();
      handleUpdateSeen();
    }
  }, [selectedReceiver.conversationId]);

  // HANDLE STORE MESSAGE TO DRAFT
  const storeMessageToDraft = useCallback(() => {
    if (!selectedReceiver.conversationId) return;

    const draftRaw = localStorage.getItem("draft");
    let draftList = draftRaw ? JSON.parse(draftRaw) : [];

    const currentId = conversationIdRef.current;
    const existingDraftIndex = draftList.findIndex(
      (item: any) => item.conversationId === currentId
    );

    if (existingDraftIndex !== -1) {
      const updatedDraft = {
        ...draftList[existingDraftIndex],
        draft: messageToSend,
      };
      draftList.splice(existingDraftIndex, 1);
      draftList.unshift(updatedDraft);
    } else {
      draftList.unshift({
        conversationId: currentId,
        draft: messageToSend,
      });
    }

    localStorage.setItem("draft", JSON.stringify(draftList));
  }, [selectedReceiver.conversationId]);

  // HANDLE GET MESSAGE TO DRAFT
  const handleGetMessageFromDraft = useCallback(() => {
    if (!selectedReceiver.conversationId) return;
    const draftRaw = localStorage.getItem("draft");
    let draftList = draftRaw ? JSON.parse(draftRaw) : [];
    if (draftList.length > 0) {
      const findDraft = draftList.find(
        (item: any) => item.conversationId == selectedReceiver.conversationId
      );
      // IF EXIST
      if (findDraft) {
        setMessageToSend(findDraft.draft);
        const newDrafts = draftList.filter(
          (item: any) => item.conversationId !== selectedReceiver.conversationId
        );
        localStorage.setItem("draft", JSON.stringify(newDrafts));
      } else {
        setMessageToSend("");
      }
    } else {
      setMessageToSend("");
    }
  }, [selectedReceiver.conversationId]);

  // HANDLE SEND NEW MESSAGE
  useEffect(() => {
    if (selectedReceiver.receiverId && conversationList) {
      const existingConversation = conversationList.find(
        (item: any) => item.target_user.user_id == selectedReceiver.receiverId
      );
      if (existingConversation) {
        setSelectedReceiver({
          ...selectedReceiver,
          conversationId: existingConversation.conversation_id,
          receiverAvatar: existingConversation.target_user.avatar,
          receiverName: existingConversation.target_user.profile_name,
        });
      } else {
        setConversation([]);
        setSelectedReceiver({
          ...selectedReceiver,
          conversationId: null,
        });
      }
    }
  }, [selectedReceiver.receiverId, conversationList]);

  // HANDLE GET CONVERSATION LIST
  useEffect(() => {
    const fetchConversationList = async () => {
      try {
        const response = await conversationApi.getConversattion();
        if (response.status == 200) {
          handleReorder(response.data.data);
        }
      } catch (error) {
        Helpers.notification.error("Something wrong in server");
        errorLog("ChatBox.tsx - fetchConversationList", error);
      }
      setIsLoading({ ...isLoading, conversationList: false });
    };
    if (isOpenChatbox) {
      setIsLoading({ ...isLoading, conversationList: true });
      fetchConversationList();
    }
  }, [isOpenChatbox]);

  // HANDLE REORDER CONVERSATION LIST
  const handleReorder = (rawList: any[]) => {
    if (rawList.length <= 0) return;
    const pinnedList: any[] = [];
    const unpinnedList: any[] = [];

    rawList.forEach((item: any) => {
      if (item.is_pinned) {
        pinnedList.push(item);
      } else {
        unpinnedList.push(item);
      }
    });

    const sortByTimeDesc = (a: any, b: any) =>
      new Date(b.time).getTime() - new Date(a.time).getTime();

    pinnedList.sort(sortByTimeDesc);
    unpinnedList.sort(sortByTimeDesc);

    const newList = [...pinnedList, ...unpinnedList];

    setConversationList(newList);
  };

  // HANDLE PIN CONVERSATION
  const handlePinConversation = async (conversationId: number) => {
    const newConversationList = conversationList.map((item: any) =>
      item.conversation_id === conversationId
        ? { ...item, is_pinned: !item.is_pinned }
        : item
    );
    handleReorder(newConversationList);
    try {
      await conversationApi.pinConversation({
        conversation_id: conversationId,
      });
    } catch (error) {
      Helpers.notification.error("Can not pin conversation");
      errorLog("ChatBox.tsx - handlePinConversation", error);
    }
  };

  const items: TabsProps["items"] = [
    {
      key: "1",
      label: "Contacts",
      children: (
        <div className={`chatbox-list-item ${isExpanded ? "expanded" : ""}`}>
          {isLoading.conversationList ? (
            <div className="p-3">
              <Skeleton avatar active />
              <Skeleton avatar active />
              <Skeleton avatar active />
            </div>
          ) : (
            <>
              <div className="highlight"></div>
              {conversationList?.map((item: any, index: number) => {
                return (
                  <ConversationItem
                    key={index}
                    data={item}
                    handlePinConversation={handlePinConversation}
                    setConversation={setConversation}
                  />
                );
              })}
            </>
          )}
        </div>
      ),
    },
    {
      key: "2",
      label: "Orders",
      children: "Content of Tab Pane 2",
    },
  ];

  // HANDLE SEND MESSAGE
  const handleSendMessage = async () => {
    var mess = messageToSend.trim();

    if (selectedFiles.length > 0) {
      const newMessage = {
        message: null,
        attachment_urls: [...imageUrls],
        files: [...selectedFiles],
        time: new Date().toISOString(),
        is_owner: true,
      };
      setConversation([newMessage, ...conversation]);
    } else {
      if (mess == "") return;
      const newMessage = {
        message: mess,
        time: new Date().toISOString(),
        is_owner: true,
      };
      setConversation([newMessage, ...conversation]);
      setMessageToSend("");
    }
    setIsSendingImage(true);

    const formData = new FormData();
    formData.append("receiver_id", selectedReceiver.receiverId);
    formData.append(
      "reference_product_id",
      selectedReceiver.referenceProduct
        ? selectedReceiver.referenceProduct.productId
        : null
    );
    if (selectedFiles.length == 0) {
      formData.append("message", mess);
    }
    formData.append("message_type", selectedFiles.length > 0 ? "file" : "text");
    selectedFiles.forEach((file) => {
      formData.append("attachments", file);
    });
    setSelectedFiles([]);
    setImageUrls([]);
    try {
      const response = await conversationApi.sendMessage(formData);
      if (response.status == 201) {
        handleUpdateConversationList(response.data.data);
        setIsSendingImage(false);
      }
    } catch (error) {
      Helpers.notification.error("Something wrong in server");
      errorLog("ChatBox.tsx - handleSendMessage", error);
    }
  };

  // UPDATE CONVERSATION LIST AFTER RECEIVED MESSAGE
  const handleUpdateConversationList = (dataToUpdate: IConversationList) => {
    if (!Array.isArray(conversationList)) return;
    const targetConversation = conversationList.find(
      (item: any) => item.conversation_id == dataToUpdate.conversation_id
    );
    if (targetConversation) {
      targetConversation.last_message = dataToUpdate.message;
      targetConversation.time = dataToUpdate.time;
      targetConversation.is_owner = dataToUpdate.is_owner;
      targetConversation.is_viewed = dataToUpdate.is_viewed;
      handleReorder([
        targetConversation,
        ...conversationList.filter(
          (item) => item.conversation_id !== dataToUpdate.conversation_id
        ),
      ]);
    } else {
      const newConversation = {
        conversation_id: dataToUpdate.conversation_id,
        last_message: dataToUpdate.message,
        time: dataToUpdate.time,
        is_owner: dataToUpdate.is_owner,
        is_viewed: dataToUpdate.is_viewed,
        target_user: dataToUpdate.target_user,
      };
      handleReorder([newConversation, ...conversationList]);
    }
  };

  // HANDLE SEND IMAGE
  let timeout: any;
  const handleFileChange = ({ fileList }: any, type: string) => {
    if (!fileList) return;
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => {
      const newFiles = fileList
        .map((file: any) => {
          if (
            type == "image" &&
            !Validates.validateImageFile(file.originFileObj)
          ) {
            Helpers.notification.warning(`${file.name} is not an image file!`);
            return null;
          }
          if (!Validates.validateFileSize(file.originFileObj, 5)) {
            Helpers.notification.warning(`${file.name} exceeds the 5 MB limit`);
            return null;
          }

          if (
            type == "file" &&
            !Validates.validateExcelFile(file.originFileObj) &&
            !Validates.validatePdfFile(file.originFileObj) &&
            !Validates.validateWordFile(file.originFileObj)
          ) {
            Helpers.notification.warning(`${file.name} file is invalid`);
            return null;
          }

          return file.originFileObj;
        })
        .filter((file: any) => file != null);

      setSelectedFiles(newFiles);

      let newImageUrls: any = [];

      newFiles.forEach((file: File) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          if (reader.result) {
            newImageUrls.push(reader.result as string);
            setImageUrls([...newImageUrls]);
          }
        };

        reader.readAsDataURL(file);
      });

      setTimeout(() => {
        fileList.length = 0;
      }, 0);
    }, 300);
  };

  // SEND MESSAGE WHEN HAVE UPLOAD FILES
  useEffect(() => {
    if (selectedFiles.length > 0 && imageUrls.length > 0) {
      handleSendMessage();
    }
  }, [selectedFiles, imageUrls]);

  // HANDLE DOWNLOAD FILE
  const handleDownloadFile = async (cdn: string) => {
    const extension = Helpers.isCDNUrl(cdn)
      ? Helpers.getFileExtension(cdn)
      : Helpers.getExtensionFromDataUrl(cdn);

    if (extension == "pdf") {
      try {
        const response = await fetch(cdn);
        const blob = await response.blob();
        const urlBlob = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.style.display = "none"; // ẩn tuyệt đối
        anchor.href = urlBlob;
        anchor.setAttribute("download", cdn.split("/").pop() || "file");
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        URL.revokeObjectURL(urlBlob);
      } catch (error) {
        console.error("Lỗi tải file", error);
      }
    } else {
      const link = document.createElement("a");
      link.href = cdn;
      link.setAttribute("download", cdn.split("/").pop() || "file");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // EFFECT FOR SELECTED CONVERSATION
  useEffect(() => {
    const items = document.querySelectorAll(".ConversationItem");
    const highlight: any = document.querySelector(".highlight");
    if (selectedReceiver.conversationId) {
      items.forEach((item, index) => {
        if (item.classList.contains("active")) {
          highlight.style.background = `var(--primary-50)`;
          highlight.style.transform = `translateY(${index * 68 + 5 * index}px)`;
        }
      });
    } else {
      if (highlight) {
        highlight.style.background = `transparent`;
      }
    }
  }, [selectedReceiver.conversationId, conversationList]);

  // CALCULATE PRODUCT
  const calculateTotalPrice = useMemo(() => {
    let total = 0;
    let pricePerTons = 0;
    if (
      selectedReceiver.referenceProduct &&
      selectedReceiver.referenceProduct.productId
    ) {
      pricePerTons = convertCurrency(
        selectedReceiver.referenceProduct?.pricePerUnit,
        "USD",
        units.currency
      );
      total =
        pricePerTons *
        (selectedReceiver.referenceProduct.quantity *
          (selectedReceiver.referenceProduct?.weightPerBale / 1000));
    }
    return { pricePerTons, total };
  }, [selectedReceiver.referenceProduct]);

  // HANDLE ADD TO CART
  const handleAddToCard = () => {
    const payload = {
      product_id: selectedReceiver.referenceProduct?.productId,
      quantity: selectedReceiver.referenceProduct?.quantity,
    };
    addToCart(payload);
  };

  return (
    <div
      className={`ChatBox ${!isOpenChatbox ? "hide" : ""} ${
        isExpanded ? "expanded" : ""
      }`}
    >
      <div className="chatbox-wrap">
        <div className="chatbox-header">
          <div className="chatbox-header-title">Chat</div>
          <div className="chatbox-control">
            <Button
              onClick={() => setIsExpanded(!isExpanded)}
              size="small"
              type="link"
              icon={<ExpandOutlined />}
            />
            <Button
              onClick={() => {
                setSelectedReceiver({
                  receiverId: null,
                  conversationId: null,
                  receiverAvatar: null,
                  receiverName: null,
                  referenceProduct: null,
                });
                setIsOpenChatbox(false);
              }}
              size="small"
              type="link"
              icon={<CloseOutlined />}
            />
          </div>
        </div>
        <div className="chatbox-body">
          <Row>
            <Col span={9}>
              <div className="chatbox-side-group">
                <div className="chatbox-search">
                  <Input variant="filled" placeholder="Search" />
                </div>
                <Tabs className="chatbox-tabs" accessKey="1" items={items} />
              </div>
            </Col>
            <Col span={15}>
              {selectedReceiver.conversationId ||
              selectedReceiver.receiverId ? (
                <div className="conversations-group">
                  <div className="conversation-header">
                    <Flex gap={8} align="center">
                      <div className="avatar-group">
                        <Link
                          to={`/producer/detail?producerId=${selectedReceiver.receiverId}`}
                        >
                          {selectedReceiver.receiverAvatar ? (
                            <Avatar
                              size={45}
                              src={selectedReceiver?.receiverAvatar}
                            />
                          ) : (
                            <Avatar size={45}>
                              {selectedReceiver?.receiverName[0].toUpperCase()}
                            </Avatar>
                          )}
                        </Link>
                        <div className="user-status"></div>
                      </div>
                      <div className="name-group fw-bold">
                        <Link
                          to={`/producer/detail?producerId=${selectedReceiver.receiverId}`}
                        >
                          {selectedReceiver?.receiverName}
                        </Link>
                      </div>
                    </Flex>
                    {selectedReceiver.referenceProduct && (
                      <div
                        className={`reference-product-wrap ${
                          selectedReceiver.referenceProduct && "display"
                        }`}
                      >
                        <Divider className="divider" />
                        <Flex gap={8} align="center">
                          <div className="conversation-product-image">
                            <Image
                              src={
                                selectedReceiver?.referenceProduct?.productImage
                              }
                              className="product-image"
                            />
                          </div>
                          <div className="conversation-product-info">
                            <div className="product-name">
                              <Link
                                to={`/product/detail?productId=${selectedReceiver.referenceProduct.productId}`}
                              >
                                {
                                  selectedReceiver?.referenceProduct
                                    ?.productType
                                }
                              </Link>
                            </div>
                            <div className="product-price">
                              Price:
                              <span>
                                $
                                {Helpers.formatNumber(
                                  calculateTotalPrice.pricePerTons,
                                  2
                                )}
                                /{"Ton"}
                              </span>
                            </div>
                            <div className="product-quantity">
                              Quantity:{" "}
                              <span>
                                {selectedReceiver?.referenceProduct?.quantity}{" "}
                                Bales
                              </span>
                            </div>
                          </div>
                          <div className="conversation-product-util">
                            <div className="d-flex gap-2">
                              <div className="product-category">Hay</div>
                              <Button
                                className="add-cart-btn"
                                size="small"
                                onClick={handleAddToCard}
                                icon={<ShoppingCartOutlined />}
                              />
                            </div>
                            <div className="product-total">
                              Total: $
                              {Helpers.formatNumber(
                                calculateTotalPrice.total,
                                2
                              )}
                            </div>
                          </div>
                        </Flex>
                      </div>
                    )}
                  </div>
                  {isLoading.conversationDetail ? (
                    <div
                      className={`conversation-list-wait ${
                        isExpanded && "expanded"
                      } ${selectedReceiver.referenceProduct && "reference"}`}
                    >
                      <LoadingOutlined
                        style={{ fontSize: 40, color: "#949494FF" }}
                      />
                    </div>
                  ) : (
                    <div
                      className={`conversation-list ${
                        isExpanded && "expanded"
                      } ${selectedReceiver.referenceProduct && "reference"}`}
                    >
                      {conversation.map((item, index) => {
                        // IF ATTACHMENTS
                        if (
                          item.attachment_urls &&
                          item.attachment_urls.length > 0
                        ) {
                          let extension = "";
                          const firstUrl = item.attachment_urls[0];
                          if (Helpers.isCDNUrl(item.attachment_urls[0])) {
                            extension = Helpers.getFileExtension(firstUrl);
                          } else {
                            extension =
                              Helpers.getExtensionFromDataUrl(firstUrl);
                          }
                          // IF HAVE TEXT
                          if (item.message && item.message.trim() !== "") {
                            return (
                              <div
                                key={index}
                                className={`conversation-item-wrap ${
                                  item.is_owner ? "owner" : ""
                                }`}
                              >
                                <Tooltip
                                  arrow={false}
                                  placement={item.is_owner ? "left" : "right"}
                                  title={Helpers.formatDateTime(
                                    item.time,
                                    true
                                  )}
                                >
                                  <div className={"conversation-item"}>
                                    <div className="message">
                                      {item.message}
                                    </div>
                                    <div className="file-list">
                                      <Row gutter={8}>
                                        {item.attachment_urls.map(
                                          (url, idx) => {
                                            return (
                                              <Col
                                                span={
                                                  item.attachment_urls!
                                                    .length == 1
                                                    ? 24
                                                    : 12
                                                }
                                              >
                                                <div
                                                  className="file-item"
                                                  key={idx}
                                                  onClick={() =>
                                                    handleDownloadFile(url)
                                                  }
                                                >
                                                  <div className="file-icon">
                                                    <img
                                                      src={
                                                        extension == "xls" ||
                                                        extension == "xlsx"
                                                          ? excelIcon
                                                          : extension == "pdf"
                                                          ? pdfIcon
                                                          : docsIcon
                                                      }
                                                      width={20}
                                                    />
                                                  </div>
                                                  <div className="file-name">
                                                    {Helpers.isCDNUrl(url)
                                                      ? Helpers.extractFileName(
                                                          url
                                                        )
                                                      : item.files?.[idx].name}
                                                  </div>
                                                </div>
                                              </Col>
                                            );
                                          }
                                        )}
                                      </Row>
                                    </div>
                                    {index == 0 && item.is_owner && (
                                      <div className="time">
                                        <CheckOutlined className="me-1" />
                                        Sent
                                      </div>
                                    )}
                                  </div>
                                </Tooltip>
                              </div>
                            );
                          }
                          // IF IMAGES
                          if (Helpers.isImage(extension)) {
                            return (
                              <div
                                key={index}
                                className={`conversation-item-wrap image ${
                                  item.is_owner ? "owner" : ""
                                }`}
                              >
                                <Tooltip
                                  arrow={false}
                                  placement={item.is_owner ? "left" : "right"}
                                  title={Helpers.formatDateTime(
                                    item.time,
                                    true
                                  )}
                                >
                                  <div className={"conversation-item"}>
                                    <div className="image-group">
                                      {item.attachment_urls.map(
                                        (url, index) => {
                                          return (
                                            <Image
                                              src={url}
                                              key={index}
                                              className="image-item"
                                            />
                                          );
                                        }
                                      )}
                                    </div>
                                    {index == 0 &&
                                      item.is_owner &&
                                      (isSendingImage ? (
                                        <div className="time mt-1">
                                          <FieldTimeOutlined className="me-1" />
                                          Sending
                                        </div>
                                      ) : (
                                        <div className="time mt-1">
                                          <CheckOutlined className="me-1" />
                                          Sent
                                        </div>
                                      ))}
                                  </div>
                                </Tooltip>
                              </div>
                            );
                          }
                          // IF FILES
                          return (
                            <div
                              key={index}
                              className={`conversation-item-wrap file ${
                                item.is_owner ? "owner" : ""
                              }`}
                            >
                              <Tooltip
                                arrow={false}
                                placement={item.is_owner ? "left" : "right"}
                                title={Helpers.formatDateTime(item.time, true)}
                              >
                                <div
                                  className={`conversation-item ${extension}`}
                                >
                                  {item.attachment_urls.map((url, indx) => {
                                    return (
                                      <div className="file-item" key={indx}>
                                        <div className="file-icon">
                                          <img
                                            src={
                                              extension == "xls" ||
                                              extension == "xlsx"
                                                ? excelIcon
                                                : extension == "pdf"
                                                ? pdfIcon
                                                : docsIcon
                                            }
                                            width={30}
                                          />
                                        </div>
                                        <div className="file-name">
                                          {Helpers.isCDNUrl(url)
                                            ? Helpers.extractFileName(url)
                                            : item.files?.[indx].name}
                                        </div>
                                        <Button
                                          size="small"
                                          className="download-btn"
                                          icon={<DownloadOutlined />}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDownloadFile(url);
                                          }}
                                        />
                                      </div>
                                    );
                                  })}
                                  {index == 0 &&
                                    item.is_owner &&
                                    (isSendingImage ? (
                                      <div className="time mt-1">
                                        <FieldTimeOutlined className="me-1" />
                                        Sending
                                      </div>
                                    ) : (
                                      <div className="time mt-1">
                                        <CheckOutlined className="me-1" />
                                        Sent
                                      </div>
                                    ))}
                                </div>
                              </Tooltip>
                            </div>
                          );
                        }
                        // IF TEXT
                        return (
                          <div
                            key={index}
                            className={`conversation-item-wrap ${
                              item.is_owner ? "owner" : ""
                            }`}
                          >
                            <Tooltip
                              arrow={false}
                              placement={item.is_owner ? "left" : "right"}
                              title={Helpers.formatDateTime(item.time, true)}
                            >
                              <div className={"conversation-item"}>
                                <div className="message">{item.message}</div>
                                {index == 0 && item.is_owner && (
                                  <div className="time">
                                    <CheckOutlined className="me-1" />
                                    Sent
                                  </div>
                                )}
                              </div>
                            </Tooltip>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div className="conversation-control">
                    <div className="conversation-action">
                      <Upload
                        multiple
                        name="file"
                        onChange={(fileList) =>
                          handleFileChange(fileList, "file")
                        }
                        customRequest={() => {
                          return;
                        }}
                        showUploadList={false}
                      >
                        <Button
                          className="action-btn"
                          type="link"
                          icon={<img src={attachIcon} width={18} />}
                        />
                      </Upload>
                      <Upload
                        multiple
                        name="file"
                        onChange={(fileList) =>
                          handleFileChange(fileList, "image")
                        }
                        customRequest={() => {
                          return;
                        }}
                        showUploadList={false}
                      >
                        <Button
                          className="action-btn"
                          type="link"
                          icon={<img src={imageIcon} width={18} />}
                        />
                      </Upload>
                      <Button
                        className="action-btn"
                        onClick={() => setShowPicker((prev) => !prev)}
                        type="link"
                        icon={<img src={emojiIcon} width={18} />}
                      />
                      <Button
                        className={`action-btn send ${
                          messageToSend.trim() !== "" && "ready"
                        }`}
                        type="link"
                        icon={<SendOutlined />}
                        onClick={handleSendMessage}
                      />
                      {showPicker && (
                        <div
                          style={{
                            position: "absolute",
                            bottom: 45,
                            left: 10,
                            zIndex: 10,
                          }}
                          ref={pickerRef}
                        >
                          <EmojiPicker
                            height={300}
                            emojiStyle={EmojiStyle.APPLE}
                            searchDisabled
                            previewConfig={{ showPreview: false }}
                            onEmojiClick={(emoji) => {
                              setMessageToSend((prev) => prev + emoji.emoji);
                              textBoxRef.current.focus();
                            }}
                          />
                        </div>
                      )}
                    </div>
                    <TextArea
                      className="conversation-input"
                      placeholder="What's on your mind?"
                      variant="borderless"
                      value={messageToSend}
                      ref={textBoxRef}
                      onChange={(e) => {
                        setMessageToSend(e.target.value);
                      }}
                      onKeyUp={(e) => {
                        if (e.key == "Enter") {
                          handleSendMessage();
                        }
                      }}
                    />
                  </div>
                </div>
              ) : (
                <div
                  className={`no-conversation-wrap ${
                    isExpanded ? "expanded" : ""
                  }`}
                >
                  <div className="no-conversation">
                    <div className="no-conversation-icon">
                      <UserAddOutlined />
                    </div>
                    <div className="no-conversation-title">
                      Select a conversation
                    </div>
                  </div>
                </div>
              )}
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
};

interface IConversationItem {
  data: any;
  handlePinConversation: (conversationId: number) => void;
  setConversation: (conversation: any[]) => void;
}
const ConversationItem: React.FC<IConversationItem> = ({
  data,
  handlePinConversation,
  setConversation,
}) => {
  const {
    selectedReceiver,
    setSelectedReceiver,
    messageCount,
    setMessageCount,
  } = useUser();

  const handleSelectConversation = () => {
    if (!data.is_viewed && !data.is_owner) {
      setMessageCount(messageCount - 1);
    }
    if (selectedReceiver.conversationId == data.conversation_id) return;
    setConversation([]);
    setSelectedReceiver({
      receiverId: data.target_user.user_id,
      conversationId: data.conversation_id,
      receiverName: data.target_user?.profile_name,
      receiverAvatar: data?.target_user?.avatar,
      referenceProduct: null,
    });
  };

  return (
    <div
      className={`ConversationItem ${
        selectedReceiver.conversationId == data.conversation_id ? "active" : ""
      }`}
      onClick={handleSelectConversation}
    >
      <div className="conversation-item-wrap">
        <div className="avatar-group">
          {data?.target_user?.avatar ? (
            <Avatar size={45} src={data?.target_user?.avatar} />
          ) : (
            <Avatar size={45}>
              {data?.target_user?.profile_name[0].toUpperCase()}
            </Avatar>
          )}
          <div className="user-status"></div>
        </div>
        <div className="user-group">
          <div className="user-name">{data?.target_user?.profile_name}</div>
          <div className="last-message">
            {data.is_owner ? "You: " : ""}
            {data.last_message ? data.last_message : "[File]"}
          </div>
        </div>
        <div className="chat-item-util">
          <div className="last-message-date">
            {Helpers.formatDateTime(data.time, true)}
          </div>
          <div className="conversation-other">
            {!data.is_viewed &&
              selectedReceiver.conversationId !== data.conversation_id &&
              !data.is_owner && <div className="new-message-point"></div>}
            <Button
              size="small"
              type="link"
              className="pin-btn"
              icon={
                data.is_pinned ? (
                  <PushpinFilled style={{ color: "#f9a72e" }} />
                ) : (
                  <PushpinOutlined />
                )
              }
              onClick={(e) => {
                e.stopPropagation();
                handlePinConversation(data.conversation_id);
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatBox;
